import type { AppInventoryOptionType, AppInventoryState } from '@openx/types/targeting/appInventory.ts';
import type {
  IntersectOptionPayload,
  InventoryAndContentOptionPayload,
} from '@openx/types/targeting/inventoryAndContent.ts';
import type { ComparisonType } from '@openx/types/targeting/targetingValuesTypes.ts';
import type { AnyAction } from '@reduxjs/toolkit';
import { useCallback, useMemo } from 'react';
import { getItemsCount } from '../../../../InventoryAndContent/InventoryAndContentTopBox/utils/getItemsCount.ts';
import { setComparisonOpValue } from '../../../../shared/AppInventory/inventoryHelpers.ts';
import { INVENTORY_LIMITS } from '../../../../shared/InventoryAndContent/constants.ts';

export type UseSetupInventoryAndContentProps = {
  targetingParams: AppInventoryState;
  contentOption: AppInventoryOptionType;
  comparisonType: ComparisonType;
  dispatchInventory: (action: AnyAction) => void;
};

export const useSetupAppInventory = ({
  dispatchInventory,
  targetingParams,
  contentOption,
  comparisonType,
}: UseSetupInventoryAndContentProps) => {
  const inventoryContent = targetingParams[contentOption] as InventoryAndContentOptionPayload | IntersectOptionPayload;

  const comparisonFieldCriteria = useMemo(
    () => (Array.isArray(inventoryContent?.val) ? inventoryContent?.val : inventoryContent?.val?.[comparisonType]),
    [comparisonType, inventoryContent?.val]
  );

  const itemsCount = useMemo(() => getItemsCount(inventoryContent?.val), [inventoryContent?.val]);

  const addItems = useCallback(
    (values: string[]) => {
      const totalItemsCount = getItemsCount(inventoryContent?.val);
      const limit = INVENTORY_LIMITS[contentOption];
      const remaining = Math.max(0, limit - totalItemsCount);
      const valuesToAdd = values.slice(0, remaining);

      if (!valuesToAdd.length) return;

      const currentComparisonData = inventoryContent?.val?.[comparisonType] || [];
      const updatedValues = [...currentComparisonData, ...valuesToAdd];

      dispatchInventory({
        payload: { comparisonType, values: updatedValues },
        type: setComparisonOpValue[contentOption],
      });
    },
    [comparisonType, contentOption, dispatchInventory, inventoryContent]
  );

  return {
    itemsCount,
    addItems,
    comparisonFieldCriteria,
  };
};
